import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getRestaurants,
  getEmployees,
  getEmployeeProfiles,
  getMonthlyAttendance,
} from "../Components/api/SalaryCalculationApi";

/* ═══════════════════════════════════════════════════
   FORMATTING HELPERS
   ═══════════════════════════════════════════════════ */
const formatINR = (value) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

/** workingMinutes (e.g. 12657) -> "210h 57m" — raw minutes aren't meaningful
    to read at a glance, so every place that shows working time uses this. */
const minutesToHours = (minutes) => {
  const total = Number(minutes) || 0;
  const hrs = Math.floor(total / 60);
  const mins = total % 60;
  return `${hrs}h ${mins}m`;
};

/* ═══════════════════════════════════════════════════
   SMALL PRESENTATIONAL PIECES
   ═══════════════════════════════════════════════════ */
const KpiCard = ({ label, value, accent = "slate", highlight = false, subtext }) => {
  const accents = {
    slate:   "text-slate-900",
    emerald: "text-emerald-600",
    rose:    "text-rose-600",
    teal:    "text-teal-600",
  };
  return (
    <div
      className={`rounded-xl bg-white p-4 shadow-sm ring-1 ${
        highlight ? "ring-teal-200 ring-2" : "ring-slate-200"
      }`}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`mt-1.5 text-xl font-bold tabular-nums ${accents[accent]}`}>
        {value}
      </p>
      {subtext && <p className="mt-0.5 text-xs text-slate-400">{subtext}</p>}
    </div>
  );
};

/* AttendanceStat accepts a full Tailwind text-color class (e.g. "text-emerald-600"),
   not a bare color name — this was the source of the "text-text-emerald-600" bug. */
const AttendanceStat = ({ label, value, accent = "text-slate-900", subtext }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className={`mt-0.5 text-base font-semibold tabular-nums ${accent}`}>
      {value}
    </p>
    {subtext && <p className="mt-0.5 text-[11px] text-slate-400">{subtext}</p>}
  </div>
);

const Skeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-24 rounded-xl bg-slate-200/70" />
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-20 rounded-xl bg-slate-200/70" />
      ))}
    </div>
    <div className="h-32 rounded-xl bg-slate-200/70" />
  </div>
);

const ErrorBanner = ({ message, onRetry }) => (
  <div className="flex items-start justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50 p-4">
    <div>
      <p className="text-sm font-semibold text-rose-700">Something went wrong</p>
      <p className="mt-0.5 text-sm text-rose-600">{message}</p>
    </div>
    {onRetry && (
      <button
        onClick={onRetry}
        className="shrink-0 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
      >
        Retry
      </button>
    )}
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center">
    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-teal-50 text-teal-600">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
    <p className="text-sm font-semibold text-slate-700">{title}</p>
    <p className="mt-1 max-w-xs text-sm text-slate-500">{description}</p>
  </div>
);

/* ═══════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════ */
const SalaryCalculation = () => {
  const [restaurants, setRestaurants]   = useState([]);
  const [selectedRestId, setSelectedRestId] = useState("");

  const [employees, setEmployees] = useState([]);
  const [profiles, setProfiles]   = useState([]);
  const [attendance, setAttendance] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear]   = useState(new Date().getFullYear());

  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [restaurantsError,   setRestaurantsError]   = useState(null);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError,   setDetailError]   = useState(null);

  // Tracks the in-flight request per selection so a fast restaurant/month/year
  // switch cancels the previous request instead of letting it resolve later
  // and overwrite fresher state.
  const abortRef = useRef(null);

  /* ─── LOAD RESTAURANTS ─── */
  const loadRestaurants = useCallback(async () => {
    setRestaurantsLoading(true);
    setRestaurantsError(null);
    try {
      const data = await getRestaurants();
      setRestaurants(data);
    } catch (error) {
      console.error("Restaurant Load Error:", error);
      setRestaurantsError(error?.message || "Failed to load restaurants.");
    } finally {
      setRestaurantsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRestaurants();
  }, [loadRestaurants]);

  /* ─── LOAD EMPLOYEES + PROFILES + ATTENDANCE FOR A RESTAURANT/MONTH/YEAR ─── */
  const loadRestaurantDetail = useCallback(
    async (restId, forMonth, forYear) => {
      // Cancel any request still in flight from a previous selection.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;

      setDetailLoading(true);
      setDetailError(null);

      try {
        const employeeData = await getEmployees({ signal });
        const filteredEmployees = employeeData.filter(
          (emp) => emp.activeRestId === restId
        );
        setEmployees(filteredEmployees);

        const profileData = await getEmployeeProfiles(restId, { signal });
        setProfiles(profileData);

        const attendanceData = await getMonthlyAttendance({
          restId,
          month: forMonth,
          year: forYear,
          signal,
        });
        setAttendance(attendanceData);
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
        console.error("Restaurant Detail Load Error:", error);
        setDetailError(error?.message || "Failed to load employee data for this restaurant.");
        setEmployees([]);
        setProfiles([]);
        setAttendance([]);
      } finally {
        if (!signal.aborted) setDetailLoading(false);
      }
    },
    []
  );

  const handleRestaurantChange = (e) => {
    const restId = Number(e.target.value);
    setSelectedRestId(restId);
    setCurrentIndex(0);
  };

  // Single source of truth for (re)loading detail data: fires whenever the
  // selected restaurant, month, or year changes.
  useEffect(() => {
    if (selectedRestId) {
      loadRestaurantDetail(selectedRestId, month, year);
    }
  }, [selectedRestId, month, year, loadRestaurantDetail]);

  // Cancel any pending request on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  const currentEmployee = employees[currentIndex];

  const currentProfile = profiles.find(
    (p) => p.empId === currentEmployee?.empId
  );

  const currentAttendance = attendance.find(
    (a) => a.empId === currentEmployee?.empId
  );

  /* ─── EXPECTED HOURS / EFFICIENCY ─────────────────────────────────
     currentProfile.expectedWorkingMinutes is the employee's expected
     minutes PER DAY (it's also the base used server-side for the
     fullDayMinPercent/halfDayMinPercent/quarterDayMinPercent thresholds
     in the monthly summary). Multiply by the month's working days to
     get an expected total, then compare against actual workingMinutes.
  ─────────────────────────────────────────────────────────────────── */
  const expectedMinutesPerDay = currentProfile?.expectedWorkingMinutes || 0;
  const workingDaysInMonth =
    currentAttendance?.workingDays ?? currentAttendance?.totalDaysInMonth ?? 0;
  const expectedTotalMinutes = expectedMinutesPerDay * workingDaysInMonth;
  const actualWorkingMinutes = currentAttendance?.workingMinutes || 0;

  const efficiencyPct =
    expectedTotalMinutes > 0
      ? (actualWorkingMinutes / expectedTotalMinutes) * 100
      : null;

  const efficiencyAccent =
    efficiencyPct === null
      ? "text-slate-400"
      : efficiencyPct >= 90
      ? "text-emerald-600"
      : efficiencyPct >= 70
      ? "text-orange-600"
      : "text-rose-600";

  /* ─── PAID DAYS CROSS-CHECK (display only) ────────────────────────
     currentAttendance.paidDays is the authoritative figure used for
     salary — it already accounts for leave credit, so it can be
     legitimately higher than days literally worked. This second figure
     is purely a sanity-check readout (actual hours ÷ expected hours/day)
     so a large gap between the two is visible at a glance; it is never
     used in the salary math itself.
  ─────────────────────────────────────────────────────────────────── */
  const hoursBasedPaidDays =
    expectedMinutesPerDay > 0
      ? actualWorkingMinutes / expectedMinutesPerDay
      : null;

  /* ─── LEAVE CREDIT EARNED (computed, not read from the API) ───────
     The API's leaveCreditUsed/leaveCreditAllowed fields don't reflect
     the actual policy, so this is derived directly from attendance:

       attendanceDays = fullDays + halfDays×0.5 + quarterDays×0.25
       >= 100% of month days  -> 2 credits earned
       >= 50%  of month days  -> 1 credit earned
       otherwise              -> 0 credits earned

     Only meaningful when weeklyOffPaid is false: if weekly-offs are
     already paid, this restaurant's leave-credit tracking doesn't apply
     the same way, so the stat is hidden entirely rather than shown with
     a misleading number.
  ─────────────────────────────────────────────────────────────────── */
  const showLeaveCredit = currentAttendance?.weeklyOffPaid === false;

  const totalDaysInMonth = currentAttendance?.totalDaysInMonth || 0;
  const totalAttendanceDays =
    (currentAttendance?.fullDays || 0) +
    (currentAttendance?.halfDays || 0) * 0.5 +
    (currentAttendance?.quarterDays || 0) * 0.25;

  const leaveCreditEarned =
    totalDaysInMonth <= 0
      ? 0
      : totalAttendanceDays >= totalDaysInMonth
      ? 2
      : totalAttendanceDays >= totalDaysInMonth * 0.5
      ? 1
      : 0;

  const leaveCreditPercentage =
    totalDaysInMonth > 0 ? (leaveCreditEarned / 2) * 100 : null;

  /* ─── PAID DAYS (computed, consistent with Leave Credit Used) ─────
     Previously this used the API's raw paidDays field (attendance +
     leaveCreditUsed from possibly-banked credit), which could disagree
     with the "Leave Credit Used" tile — that tile only counts credit
     EARNED this month from this month's attendance. Using two different
     sources for what looks like the same concept was the source of the
     "why does it say 2 paid leave when Leave Credit Used shows 0/2"
     confusion.

     Paid Days is now computed the same way everywhere on this page:
       paidDays = attendance days present (this month) + leaveCreditEarned (this month)
     0% attendance this month -> 0 leave credit earned -> 0 paid days,
     consistently, in every figure derived from it (salary included).
  ─────────────────────────────────────────────────────────────────── */
  const computedPaidDays = totalAttendanceDays + leaveCreditEarned;

  const salaryData = (() => {
    if (!currentProfile || !currentAttendance) return null;

    const basicSalary = currentProfile.salary?.basic || 0;
    const totalDays = currentAttendance.totalDaysInMonth || 0;
    const paidDays = computedPaidDays;
    const perDaySalary = totalDays > 0 ? basicSalary / totalDays : 0;
    const grossSalary = perDaySalary * paidDays;

    const deductions =
      (currentProfile.salary?.advanceDeduction || 0) +
      (currentProfile.salary?.loanEMI || 0) +
      (currentProfile.salary?.staffExp || 0) +
      (currentProfile.salary?.fineApplied || 0);

    const netSalary = grossSalary - deductions;

    return { basicSalary, paidDays, grossSalary, deductions, netSalary };
  })();

  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (currentIndex < employees.length - 1) setCurrentIndex((prev) => prev + 1);
  };

  const selectedRestaurantName = restaurants.find(
    (r) => r.restId === selectedRestId
  )?.restaurantName;

  const progressPct = employees.length
    ? ((currentIndex + 1) / employees.length) * 100
    : 0;

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">

        {/* ── HEADER ── */}
        <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:flex-row md:items-center md:justify-between md:p-5">
          <div>
            <h1 className="text-xl font-bold text-slate-900 md:text-2xl">Salary Calculation</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Calculate employee salary based on monthly attendance
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
            <div className="flex items-center gap-2 rounded-full bg-teal-50 px-3.5 py-1.5 text-sm font-medium text-teal-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {MONTH_NAMES[month - 1]} {year}
            </div>
            {currentAttendance && (
              <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3.5 py-1.5 text-sm font-medium tabular-nums text-slate-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {minutesToHours(actualWorkingMinutes)} worked
              </div>
            )}
            {expectedTotalMinutes > 0 && (
              <div className="flex items-center gap-2 rounded-full bg-amber-50 px-3.5 py-1.5 text-sm font-medium tabular-nums text-amber-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m6 10V7M5 21h14a1 1 0 001-1V4a1 1 0 00-1-1H5a1 1 0 00-1 1v16a1 1 0 001 1z" />
                </svg>
                {minutesToHours(expectedMinutesPerDay)} × {workingDaysInMonth}d = {minutesToHours(expectedTotalMinutes)} expected
              </div>
            )}
          </div>
        </div>

        {/* ── FILTERS: restaurant + month + year in one row ── */}
        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Restaurant
              </label>
              {restaurantsError ? (
                <ErrorBanner message={restaurantsError} onRetry={loadRestaurants} />
              ) : (
                <select
                  value={selectedRestId}
                  onChange={handleRestaurantChange}
                  disabled={restaurantsLoading}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">
                    {restaurantsLoading ? "Loading…" : "Choose a restaurant"}
                  </option>
                  {restaurants.map((restaurant) => (
                    <option key={restaurant.restId} value={restaurant.restId}>
                      {restaurant.restaurantName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              >
                {MONTH_NAMES.map((name, index) => (
                  <option key={index} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Year
              </label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm tabular-nums outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              />
            </div>
          </div>
        </div>

        {/* ── DETAIL AREA ── */}
        {!selectedRestId ? (
          <EmptyState
            title="No restaurant selected"
            description="Choose a restaurant above to load employees and calculate salaries."
          />
        ) : detailLoading ? (
          <Skeleton />
        ) : detailError ? (
          <ErrorBanner
            message={detailError}
            onRetry={() => loadRestaurantDetail(selectedRestId, month, year)}
          />
        ) : employees.length === 0 ? (
          <EmptyState
            title="No employees found"
            description={`No active employees were found for ${selectedRestaurantName || "this restaurant"}.`}
          />
        ) : (
          currentEmployee && (
            <div className="space-y-4">

              {/* Employee identity + progress, combined into one compact card */}
              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:p-5">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-600 text-sm font-semibold text-white">
                      {getInitials(currentEmployee.empName)}
                    </div>
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-bold text-slate-900">
                        {currentEmployee.empName}
                      </h2>
                      <p className="text-xs text-slate-500">
                        Employee ID: {currentEmployee.empId}
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-semibold tabular-nums text-slate-700">
                    {currentIndex + 1} / {employees.length}
                  </div>
                </div>

                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-teal-600 transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              {/* KPI cards */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <KpiCard label="Basic Salary" value={formatINR(salaryData?.basicSalary)} />
                <KpiCard
                  label="Paid Days"
                  value={salaryData?.paidDays?.toFixed?.(1) ?? salaryData?.paidDays ?? 0}
                  subtext={
                    leaveCreditEarned > 0
                      ? `${totalAttendanceDays.toFixed(1)} attended + ${leaveCreditEarned} leave credit`
                      : hoursBasedPaidDays !== null
                      ? `${hoursBasedPaidDays.toFixed(1)} by hours worked`
                      : undefined
                  }
                />
                <KpiCard
                  label="Gross Salary"
                  value={formatINR(salaryData?.grossSalary)}
                  accent="emerald"
                />
                <KpiCard
                  label="Deductions"
                  value={formatINR(salaryData?.deductions)}
                  accent="rose"
                />
                <KpiCard
                  label="Net Salary"
                  value={formatINR(salaryData?.netSalary)}
                  accent="teal"
                  highlight
                />
              </div>

              {/* Attendance summary */}
              <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 md:p-5">
                <h3 className="mb-3 text-sm font-bold text-slate-900">
                  Attendance summary
                </h3>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
                  <AttendanceStat
                    label="Full Days"
                    value={currentAttendance?.fullDays || 0}
                    accent="text-emerald-600"
                  />
                  <AttendanceStat
                    label="Half Days"
                    value={currentAttendance?.halfDays || 0}
                    accent="text-orange-600"
                  />
                  <AttendanceStat
                    label="Quarter Days"
                    value={currentAttendance?.quarterDays || 0}
                    accent="text-pink-600"
                  />
                  <AttendanceStat
                    label="Absent Days"
                    value={currentAttendance?.absentDays || 0}
                    accent="text-rose-600"
                    subtext={
                      leaveCreditEarned > 0
                        ? `${leaveCreditEarned} covered by leave credit`
                        : undefined
                    }
                  />
                  <AttendanceStat
                    label="Suspense Days"
                    value={currentAttendance?.suspenseDays || 0}
                    accent="text-purple-600"
                  />
                  <AttendanceStat
                    label="Working Hours"
                    value={minutesToHours(currentAttendance?.workingMinutes)}
                    accent="text-teal-600"
                  />
                  <AttendanceStat
                    label="Expected Hours/Day"
                    value={minutesToHours(expectedMinutesPerDay)}
                    accent="text-slate-700"
                  />
                  <AttendanceStat
                    label="Efficiency"
                    value={efficiencyPct !== null ? `${efficiencyPct.toFixed(1)}%` : "—"}
                    accent={efficiencyAccent}
                  />
                  {showLeaveCredit && (
                    <AttendanceStat
                      label="Leave Credit Used"
                      value={
                        leaveCreditPercentage !== null
                          ? `${leaveCreditEarned}/2 (${leaveCreditPercentage.toFixed(0)}%)`
                          : `${leaveCreditEarned}/2`
                      }
                      accent={
                        leaveCreditEarned >= 2
                          ? "text-emerald-600"
                          : leaveCreditEarned >= 1
                          ? "text-orange-600"
                          : "text-rose-600"
                      }
                    />
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between gap-3">
                <button
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  className="flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous employee
                </button>

                <button
                  onClick={handleNext}
                  disabled={currentIndex === employees.length - 1}
                  className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next employee
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SalaryCalculation;